<!--
  A flex column the height of the dialog surface: the header and the footer keep
  their own height and the content takes what is left, so the device list pages
  between them instead of running underneath the footer.
-->
<div
  class="new-test-dialog w-[640px] bg-light-grey h-full flex flex-col overflow-hidden"
>
  <!-- Header -->
  <div class="flex shrink-0 justify-center items-center h-20 bg-primary-light border-b">
    <div class="flex-col text-center mx-auto">
      <h2 class="text-xl text-accent m-0 font-bold">
        <span class="flex flex-row items-center">
          @if (props.command === 'new-mslt') {
            New MSLT Test(s)
          } @else {
            New Test(s)
          }

          @if (loading) {
            <mat-spinner class="mx-2" [diameter]="20"></mat-spinner>
          }

        </span>
      </h2>
      <h2 class="text-lg text-accent m-0 mt-1 font-light">
        ({{ data.name }} {{ ageInfo() }})
      </h2>
    </div>
    <div class="mb-auto">
      <button mat-icon-button class="btn-sm h-4 w-4" (click)="onClose()">
        <mat-icon class="text-accent">close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Test details, shared by the tabbed and the MSLT layouts. -->
  <ng-template #testDetails>
    <div class="bg-white space-y-4 pb-4" [formGroup]="testForm">
      <!-- Date & Technician Fields -->
      <div class="flex flex-col space-y-2 px-8 pt-4">
        <app-date-time-picker
          class="w-full"
          label="Planned Date"
          formControlName="scheduleDateTime"
          [minDate]="currDate"
          [required]="true"
        ></app-date-time-picker>

        <!-- Technician Field (dropdown) -->
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Technician</mat-label>
          <mat-select formControlName="technicianId" name="technician">
            <mat-option *ngFor="let tech of technicians" [value]="tech.id">
              {{ tech.firstName }} {{ tech.lastName }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <div class="grid grid-cols-4 gap-4 w-full">
          <mat-form-field appearance="outline" class="col-span-2">
            <mat-label>Test Location</mat-label>
            <mat-select formControlName="location">
              <mat-option *ngFor="let t of allLocationOptions" [value]="t.value">
                {{ t.viewValue }}
              </mat-option>
            </mat-select>
            <mat-error>{{ "Required field" }}</mat-error>
          </mat-form-field>

          <!--
            Dynamic subscript so the over-the-cap message, which wraps onto several
            lines at this width, pushes the next row down instead of spilling out of
            the one-line strip Material reserves and over the fields below.
            Held to the top of its cell: with no strip reserved, a stretched field
            would grow its box to the height of the Lab field and its strip beside it.
          -->
          <mat-form-field
            appearance="outline"
            class="col-span-2 self-start"
            subscriptSizing="dynamic"
          >
            <mat-label>Number of Tests</mat-label>
            <input
              min="0"
              [max]="maxTestsPerOpening"
              type="number"
              matInput
              formControlName="testsCount"
              noNegativeInput
            />
            <mat-error *ngIf="testForm.controls['testsCount'].errors?.['max']">
              {{ maxTestsPerOpeningError }}
            </mat-error>
          </mat-form-field>

          <app-test-rooms-select
            class="col-span-2"
            [required]="false"
            *ngIf="testForm.value.location == 'Lab' && !isDirectPatient"
            [selectedValue]="testForm.value.roomId"
            (departmentSelected)="onTestRoomChange($event)"
          ></app-test-rooms-select>

          <mat-form-field appearance="outline" class="col-span-2">
            <mat-label>Test Type </mat-label>
            <mat-select formControlName="testType" required>
              <mat-option *ngFor="let t of allTypeOptions" [value]="t.value">
                {{ t.viewValue }}
              </mat-option>
            </mat-select>
            <mat-error>{{ "Required field" }}</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="col-span-2" *ngIf="isTitration">
            <mat-label>Titration Type</mat-label>
            <mat-select formControlName="titrationType">
              <mat-option *ngFor="let t of titrationTypeOptions" [value]="t.value">
                {{ t.viewValue }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    </div>
  </ng-template>

  @if (hasDeviceStep) {
    <!-- Tabs: the details, then the paged device list on a tab of its own. -->
    <mat-tab-group class="min-h-0 flex-1 bg-white" [(selectedIndex)]="selectedTab">
      <mat-tab>
        <ng-template mat-tab-label>
          <div [class.completed-tab]="testForm.valid" class="flex items-center gap-3">
            <div
              class="tab-number relative flex items-center justify-center w-7 h-7 rounded-full bg-divider p-[2px]"
            >
              <div
                class="flex items-center justify-center w-full h-full rounded-full"
              >
                <div
                  class="flex items-center justify-center w-5 h-5 rounded-full text-md text-muted font-medium"
                >
                  {{ testForm.valid ? "✓" : "1" }}
                </div>
              </div>
            </div>
            <span class="text-xs">Test Details</span>
          </div>
        </ng-template>
        <ng-container [ngTemplateOutlet]="testDetails"></ng-container>
      </mat-tab>

      <!-- Shut until the details are complete, as in the New Patient dialog. -->
      <mat-tab [disabled]="testForm.invalid">
        <ng-template mat-tab-label>
          <div [class.completed-tab]="!!selectedDevice" class="flex items-center gap-3">
            <div
              class="tab-number relative flex items-center justify-center w-7 h-7 rounded-full bg-divider p-[2px]"
            >
              <div
                class="flex items-center justify-center w-full h-full rounded-full"
              >
                <div
                  class="flex items-center justify-center w-5 h-5 rounded-full text-md text-muted font-medium"
                >
                  {{ selectedDevice ? "✓" : "2" }}
                </div>
              </div>
            </div>
            <span class="text-xs">Device</span>
          </div>
        </ng-template>
        <app-devices-selector
          class="block h-full"
          [statusFilter]="'available'"
          (deviceFormValue)="onDeviceSelected($event)"
          [className]="'patient-dialog'"
          [fillHeight]="true"
        ></app-devices-selector>
      </mat-tab>
    </mat-tab-group>
  } @else {
    <!-- An MSLT runs on the device of the test it follows, so there is no device step. -->
    <div class="min-h-0 flex-1 overflow-y-auto bg-white">
      <ng-container [ngTemplateOutlet]="testDetails"></ng-container>
    </div>
  }

  <!-- Footer -->
  <div class="shrink-0 bg-white w-full flex justify-end border-t">
    <div class="flex items-center justify-center w-full h-[50px]">
      <!-- Stepped as the New Patient dialog: the details tab only moves on, and
           Save waits on the device tab, where picking a device stays optional. -->
      @if (hasDeviceStep && selectedTab === detailsTab) {
        <button
          mat-flat-button
          color="primary"
          [disabled]="loading"
          class="w-full h-full btn-lg"
          (click)="goToDeviceTab()"
        >
          <span class="font-bold text-md">Continue to Next Step</span>
        </button>
      } @else {
        <button
          mat-flat-button
          color="primary"
          [disabled]="testForm.invalid ||
            loading ||
            !ability.canCreate(ability.subjects.TestRecord)
          "
          class="w-full h-full btn-lg"
          (click)="save()"
        >
          <span class="font-bold text-md">{{ "Save" }}</span>
        </button>
      }
    </div>
  </div>
</div>
